import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { useAuth } from '@hooks/useAuth';
import { useInactivityLogout } from '@hooks/useInactivityLogout';
import { useIdleDetection } from '@hooks/useIdleDetection';
import { useAppSelector } from '@store/hooks';
import { APP_ROUTES } from '@config/routes';
import { isMobileBrowser } from '@utils/isMobileBrowser';
import {
  registerForBrowserPushNotifications,
  startBrowserNotificationListeners,
  stopBrowserNotificationListeners,
} from '@services/browserPushService';

// Layouts - Load immediately (required for app structure)
import DashboardLayout from '@components/layouts/DashboardLayout';
import AuthLayout from '@components/layouts/AuthLayout';

// Components - Load immediately
import ProtectedRoute from '@components/common/ProtectedRoute';
import LoadingSpinner from '@components/common/LoadingSpinner';
import { UpdateNotification } from '@components/common/UpdateNotification';
import { InactivityWarningModal } from './modals/InactivityWarningModal';

// Lazy load pages for code splitting
// Authentication Pages
const LoginPage = lazy(() => import('@pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@pages/auth/ResetPasswordPage'));
const EmailVerifiedSuccess = lazy(() => import('@pages/auth/EmailVerifiedSuccess'));
const PlanSelectionPage = lazy(() => import('@pages/auth/PlanSelectionPage'));

const ClientPortalSharedPage = lazy(() => import('@pages/shared/ClientPortalSharedPage'));
const PublicInvoiceSharedPage = lazy(() => import('@pages/shared/PublicInvoiceSharedPage'));
const PublicFormSharedPage = lazy(() => import('@pages/shared/PublicFormSharedPage.tsx'));
const AppGatePage = lazy(() => import('@pages/AppGatePage'));

// Dashboard Pages
const DashboardPage = lazy(() => import('@/pages/sidebar/DashboardPage/index'));
const ProjectsPage = lazy(() => import('@/pages/sidebar/ProjectsPage/index'));
const MessagesTab = lazy(() => import('@/pages/sidebar/ProjectsPage/tabs/MessagesTab'));
const EmailsPage = lazy(() => import('@/pages/sidebar/EmailsPage'));
const ContactsPage = lazy(() => import('@/pages/sidebar/ContactsPage'));
const CalendarPage = lazy(() => import('@/pages/sidebar/CalendarPage'));
const SettingsPage = lazy(() => import('@/pages/sidebar/SettingsPage/index'));
const TemplatesPage = lazy(() => import('@/pages/sidebar/TemplatesPage/index'));
const WorkflowPage = lazy(() => import('@/pages/sidebar/WorkflowPage/index'));
const BillingPage = lazy(() => import('@/pages/sidebar/BillingPage'));
const ProcessingPage = lazy(() => import('@/pages/sidebar/ProcessingPage'));
const WidgetSettingsPage = lazy(() => import('@/pages/sidebar/SettingsPage/WidgetSettingsPage'));
const AccountSettingsPage = lazy(() => import('@/pages/sidebar/AccountSettingsPage'));
const NotificationsPage = lazy(() => import('@/pages/sidebar/NotificationsPage'));
const SupportPage = lazy(() => import('@/pages/sidebar/HelpPage/FeedbackPage'));
const ChangelogPage = lazy(() => import('@/pages/sidebar/ChangelogPage'));

const CONTINUE_STORAGE_KEY = 'drahft_app_gate_continue';

const ConversationsRoute = () => {
  const [searchParams] = useSearchParams();

  return (
    <div className="h-full bg-white">
      <MessagesTab initialConversationId={searchParams.get('id')} />
    </div>
  );
};

const App = () => {
  useAuth();
  const { isLoading, isAuthenticated } = useAppSelector(state => state.auth);
  const location = useLocation();
  const isDev = import.meta.env.DEV;

  // Enable inactivity logout only for authenticated users
  const { showWarning, timeRemaining, handleStayActive, handleLogout } = useInactivityLogout({
    warningTimeout: 25 * 60 * 1000, // 25 minutes of inactivity
    logoutTimeout: 5 * 60 * 1000, // 5 minutes after warning
    enabled: isAuthenticated, // Only enable when user is logged in
  });

  // Idle detection to pause expensive operations (not logout)
  useIdleDetection({
    idleTimeout: 2 * 60 * 1000, // 2 minutes
    enabled: isAuthenticated,
    onIdle: () => {
      if (isDev) {
        console.log('User idle - pausing background operations');
      }
      // Pause polling, reduce sync frequency, etc.
    },
    onActive: () => {
      if (isDev) {
        console.log('User active - resuming background operations');
      }
    },
  });

  useEffect(() => {
    if (!isAuthenticated) {
      stopBrowserNotificationListeners();
      return () => {};
    }

    startBrowserNotificationListeners();
    registerForBrowserPushNotifications();

    return () => {
      stopBrowserNotificationListeners();
    };
  }, [isAuthenticated]);

  // ── Mobile redirect to App Gate ────────────────────────────────────────
  // If user is on a mobile browser, authenticated, and hasn't chosen
  // "continue in browser", redirect to the /app gate.
  // Exempt: public pages (client-portal, invoice, join), auth pages, and /app itself.
  const isMobile = isMobileBrowser();
  const isExemptPath =
    location.pathname === APP_ROUTES.APP_GATE ||
    location.pathname.startsWith('/client-portal') ||
    location.pathname.startsWith('/form') ||
    location.pathname.startsWith('/invoice') ||
    location.pathname.startsWith('/join') ||
    location.pathname === APP_ROUTES.LOGIN ||
    location.pathname === APP_ROUTES.REGISTER ||
    location.pathname === APP_ROUTES.FORGOT_PASSWORD ||
    location.pathname === APP_ROUTES.RESET_PASSWORD ||
    location.pathname === APP_ROUTES.EMAIL_VERIFIED_SUCCESS;

  const hasContinuedInBrowser = (() => {
    try {
      return sessionStorage.getItem(CONTINUE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  })();

  const shouldRedirectToAppGate =
    isMobile && isAuthenticated && !isExemptPath && !hasContinuedInBrowser;

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (shouldRedirectToAppGate) {
    return (
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <AppGatePage />
      </Suspense>
    );
  }

  return (
    <>
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <Routes>
          {/* Root route sends users to auth/dashboard. Marketing site lives in apps/web. */}
          <Route
            path={APP_ROUTES.HOME}
            element={
              <Navigate to={isAuthenticated ? APP_ROUTES.DASHBOARD : APP_ROUTES.LOGIN} replace />
            }
          />

          <Route path={APP_ROUTES.CLIENT_PORTAL_PUBLIC} element={<ClientPortalSharedPage />} />
          <Route path={APP_ROUTES.INVOICE_PUBLIC} element={<PublicInvoiceSharedPage />} />
          <Route path={APP_ROUTES.FORM_PUBLIC} element={<PublicFormSharedPage />} />
          <Route path={APP_ROUTES.APP_GATE} element={<AppGatePage />} />

          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path={APP_ROUTES.LOGIN} element={<LoginPage />} />
            <Route path={APP_ROUTES.REGISTER} element={<RegisterPage />} />
            <Route path={APP_ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
            <Route path={APP_ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
            <Route path={APP_ROUTES.EMAIL_VERIFIED_SUCCESS} element={<EmailVerifiedSuccess />} />
          </Route>

          {/* Onboarding / Plan Selection — accessible to authenticated users without a backend profile */}
          <Route path={APP_ROUTES.ONBOARDING} element={<PlanSelectionPage />} />
          <Route path={APP_ROUTES.PLAN_SELECTION} element={<PlanSelectionPage />} />

          {/* Protected Dashboard Routes */}
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path={APP_ROUTES.DASHBOARD} element={<DashboardPage />} />
            <Route path={APP_ROUTES.PROJECTS} element={<ProjectsPage />} />
            <Route path={APP_ROUTES.CONVERSATIONS} element={<ConversationsRoute />} />
            <Route path={APP_ROUTES.EMAIL} element={<EmailsPage />} />
            <Route path={APP_ROUTES.NOTIFICATIONS} element={<NotificationsPage />} />
            <Route
              path="/dashboard/feedback"
              element={<Navigate to={APP_ROUTES.SUPPORT} replace />}
            />
            <Route path={APP_ROUTES.HELP} element={<Navigate to={APP_ROUTES.SUPPORT} replace />} />
            <Route path={APP_ROUTES.SUPPORT} element={<SupportPage />} />
            <Route
              path={APP_ROUTES.INVOICING_LEGACY}
              element={<Navigate to={`${APP_ROUTES.TEMPLATES}?tab=invoice`} replace />}
            />
            <Route path={APP_ROUTES.CLIENTS} element={<ContactsPage />} />
            <Route
              path={APP_ROUTES.CONTACTS_LEGACY}
              element={<Navigate to={APP_ROUTES.CLIENTS} replace />}
            />
            <Route
              path={APP_ROUTES.BOOKINGS}
              element={<Navigate to={APP_ROUTES.CALENDAR} replace />}
            />
            <Route path={APP_ROUTES.CALENDAR} element={<CalendarPage />} />
            <Route
              path={APP_ROUTES.FORMS_LEGACY}
              element={<Navigate to={`${APP_ROUTES.TEMPLATES}?tab=contracts`} replace />}
            />
            <Route path={APP_ROUTES.WORKFLOW} element={<WorkflowPage />} />
            <Route path={APP_ROUTES.TEMPLATES} element={<TemplatesPage />} />
            <Route path={APP_ROUTES.SETTINGS} element={<SettingsPage />} />
            <Route path={APP_ROUTES.BILLING} element={<BillingPage />} />
            <Route path="/processing" element={<ProcessingPage />} />
            <Route path={APP_ROUTES.WIDGET_SETTINGS} element={<WidgetSettingsPage />} />
            <Route path={APP_ROUTES.ACCOUNT_SETTINGS} element={<AccountSettingsPage />} />
            <Route path={APP_ROUTES.CHANGELOG} element={<ChangelogPage />} />
          </Route>

          {/* Catch all - 404 */}
          <Route
            path="*"
            element={
              <Navigate to={isAuthenticated ? APP_ROUTES.DASHBOARD : APP_ROUTES.LOGIN} replace />
            }
          />
        </Routes>
      </Suspense>

      {/* Inactivity Warning Modal */}
      <InactivityWarningModal
        isOpen={showWarning}
        timeRemaining={timeRemaining}
        onStayActive={handleStayActive}
        onLogout={handleLogout}
      />

      {/* Update Available Notification (production only) */}
      <UpdateNotification />
    </>
  );
};

export default App;
